import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/**
 * Universal Ad Renderer Component
 * 
 * Usage:
 * <AdRenderer zone="header_top" pagePath="/dashboard" />
 * <AdRenderer zone="sidebar_right" pagePath="/tools/resume" trigger="page_load" />
 * <AdRenderer zone="modal_popup" pagePath="/dashboard" trigger="exit_intent" />
 */

const AdRenderer = ({ 
  zone, 
  pagePath = window.location.pathname,
  trigger = 'page_load',
  className = '',
  onClose,
  showCloseButton = true
}) => {
  const [ad, setAd] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const adRef = useRef(null);

  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('ad_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('ad_session_id', sessionId);
    }
    return sessionId;
  };

  const recordEvent = useCallback(async (eventType, adData) => {
    if (!adData) return;
    
    try {
      await api.post('/advertisements/event', {
        campaign_id: adData.campaign_id,
        creative_id: adData.creative_id,
        placement_zone: zone,
        event_type: eventType,
        page_path: pagePath,
        device: getDeviceType(),
        user_id: JSON.parse(localStorage.getItem('user') || 'null')?.id,
        session_id: getSessionId(),
        ab_test_id: adData.ab_test_id || null,
        ab_test_variant_id: adData.ab_test_variant_id || null
      });
    } catch (error) {
      console.error('Error recording ad event:', error);
    }
  }, [zone, pagePath]);

  const fetchAd = useCallback(async () => {
    if (dismissed) return;
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      
      const response = await api.post('/advertisements/render', {
        page_path: pagePath,
        zones: [zone],
        device: getDeviceType(),
        user_id: user?.id,
        user_role: user?.role,
        session_id: getSessionId(),
        trigger_type: trigger
      });

      if (response.data.success && response.data.ads[zone]) {
        const adData = response.data.ads[zone];
        setAd(adData);
        adRef.current = adData;
        setVisible(true);
        
        // Record impression
        recordEvent('impression', adData);
      }
    } catch (error) {
      console.error('Error fetching ad:', error);
    }
  }, [zone, pagePath, trigger, dismissed, recordEvent]);

  const handleClick = () => {
    recordEvent('click', adRef.current);
    
    const ctaUrl = ad?.creative?.cta_url;
    if (ctaUrl) {
      if (ctaUrl.startsWith('http')) {
        window.open(ctaUrl, '_blank');
      } else {
        window.location.href = ctaUrl;
      }
    }
  };

  const handleClose = () => {
    setVisible(false);
    setDismissed(true);
    if (onClose) onClose();
  };

  useEffect(() => {
    if (trigger === 'page_load') {
      fetchAd();
    }
  }, [fetchAd, trigger]);

  // Handle exit intent
  useEffect(() => {
    if (trigger !== 'exit_intent') return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !dismissed) {
        fetchAd();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [trigger, fetchAd, dismissed]);

  // Handle scroll trigger
  useEffect(() => {
    if (trigger !== 'on_scroll') return;

    let hasTriggered = false;
    const targetPercent = 30; // Trigger at 30% scroll by default
    
    const handleScroll = () => {
      if (hasTriggered || dismissed) return;
      
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent >= targetPercent) {
        hasTriggered = true;
        fetchAd();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trigger, fetchAd, dismissed]);

  // Handle delay trigger
  useEffect(() => {
    if (trigger !== 'after_delay') return;

    const delay = 5; // 5 seconds default delay
    const timer = setTimeout(() => {
      if (!dismissed) fetchAd();
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [trigger, fetchAd, dismissed]);

  if (!visible || !ad?.creative || dismissed) return null;

  const creative = ad.creative;
  const primaryColor = creative.brand_colors?.primary || '#6366f1';
  const secondaryColor = creative.brand_colors?.secondary || '#ec4899';

  // Render based on zone type
  const isModal = ['modal_popup', 'full_screen', 'exit_intent', 'overlay'].includes(zone);
  const isSlideIn = zone === 'slide_in';
  const isToast = zone === 'toast';

  // Modal/Popup style
  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in zoom-in-95">
          {showCloseButton && (
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-1 rounded-full bg-gray-100 hover:bg-gray-200 z-10"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
          
          {/* Sponsored label - positioned at top with proper spacing */}
          <div className="px-4 pt-3 pb-2 border-b border-gray-100">
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              Sponsored
            </span>
          </div>
          
          {creative.image_url && (
            <img 
              src={creative.image_url} 
              alt={creative.title || 'Ad'}
              className="w-full h-48 object-cover"
            />
          )}
          
          <div className="p-6">
            {creative.title && (
              <h3 className="text-xl font-bold text-gray-900 mb-2">{creative.title}</h3>
            )}
            {creative.subtitle && (
              <p className="text-gray-600 mb-2">{creative.subtitle}</p>
            )}
            {creative.body && (
              <p className="text-sm text-gray-500 mb-4">{creative.body}</p>
            )}
            
            {creative.cta_text && (
              <Button
                onClick={handleClick}
                className="w-full"
                style={{ 
                  background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` 
                }}
              >
                {creative.cta_text}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            {creative.disclaimer && (
              <p className="text-xs text-gray-400 mt-3 text-center">{creative.disclaimer}</p>
            )}
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r" 
            style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }} 
          />
        </div>
      </div>
    );
  }

  // Slide-in style
  if (isSlideIn) {
    return (
      <div className={`fixed bottom-4 right-4 z-40 bg-white rounded-xl shadow-lg border max-w-sm animate-in slide-in-from-right ${className}`}>
        {showCloseButton && (
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
        
        {/* Sponsored label - positioned at top with proper spacing */}
        <div className="px-4 pt-2 pb-1">
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
            Sponsored
          </span>
        </div>
        
        <div className="px-4 pb-4">
          <div className="flex gap-3">
            {creative.image_url && (
              <img 
                src={creative.image_url} 
                alt=""
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              {creative.title && (
                <h4 className="font-semibold text-sm truncate">{creative.title}</h4>
              )}
              {creative.subtitle && (
                <p className="text-xs text-gray-500 mt-0.5">{creative.subtitle}</p>
              )}
            </div>
          </div>
          
          {creative.cta_text && (
            <Button
              onClick={handleClick}
              size="sm"
              className="w-full mt-3"
              style={{ 
                background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` 
              }}
            >
              {creative.cta_text}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Toast style
  if (isToast) {
    return (
      <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white rounded-lg shadow-lg border animate-in slide-in-from-bottom ${className}`}>
        {/* Sponsored label at top */}
        <div className="px-4 pt-2 pb-1 border-b border-gray-100">
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
            Sponsored
          </span>
        </div>
        <div className="px-4 py-3 flex items-center gap-3">
          {creative.title && <span className="font-medium text-sm">{creative.title}</span>}
          {creative.cta_text && (
            <Button onClick={handleClick} size="sm" variant="outline">
              {creative.cta_text}
            </Button>
          )}
          {showCloseButton && (
            <button onClick={handleClose} className="ml-2">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Banner/Inline style (default)
  return (
    <div 
      className={`relative bg-gradient-to-r rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${className}`}
      style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}15, ${secondaryColor}15)` }}
      onClick={handleClick}
    >
      {showCloseButton && (
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white z-10"
        >
          <X className="w-3 h-3 text-gray-500" />
        </button>
      )}
      
      {/* Sponsored label - positioned above content with proper spacing */}
      <div className="px-4 pt-2 pb-1">
        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
          Sponsored
        </span>
      </div>
      
      <div className="flex items-center gap-4 px-4 pb-4">
        {creative.image_url && (
          <img 
            src={creative.image_url} 
            alt=""
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        )}
        
        <div className="flex-1 min-w-0">
          {creative.title && (
            <h4 className="font-semibold text-gray-900">{creative.title}</h4>
          )}
          {creative.subtitle && (
            <p className="text-sm text-gray-600 mt-0.5">{creative.subtitle}</p>
          )}
          {creative.body && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{creative.body}</p>
          )}
        </div>
        
        {creative.cta_text && (
          <Button
            size="sm"
            className="flex-shrink-0"
            style={{ 
              background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` 
            }}
          >
            {creative.cta_text}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdRenderer;

// Export convenience components for specific zones
export const HeaderAd = (props) => <AdRenderer zone="header_top" {...props} />;
export const FooterAd = (props) => <AdRenderer zone="footer_bottom" {...props} />;
export const SidebarAd = (props) => <AdRenderer zone="sidebar_right" {...props} />;
export const PopupAd = (props) => <AdRenderer zone="modal_popup" trigger="after_delay" {...props} />;
export const ExitIntentAd = (props) => <AdRenderer zone="exit_intent" trigger="exit_intent" {...props} />;
